import * as React from 'react';

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText, SelectChangeEvent,
} from '@mui/material';

import _startCase from 'lodash/startCase';

import { FieldValues, UseControllerProps } from 'react-hook-form';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';
import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

interface IControlledSelect<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string,
  menuItems: Array<{
    name: string,
    value: string | null,
  }>,
  readOnly?: boolean,
  disabled?: boolean,
  disableEditPopper?: boolean,
  onChange?: (event: SelectChangeEvent<string>) => void,
  useMenuItemName?: boolean,
  isGrouped?: boolean,
}

function ControlledSelect<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  menuItems,
  readOnly,
  disabled,
  disableEditPopper,
  rules,
  onChange,
  useMenuItemName,
  isGrouped,
}: IControlledSelect<TFieldValues>) {
  const { requestUpdate } = useFormUpdateProxy();
  const {
    field,
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
    rules,
  });

  const getMenuItemName = (value: string): string | undefined => {
    if (menuItems && value) {
      const result = menuItems.find((item) => {
        const x = 0;
        return item.value === value;
      });
      return result ? result.name ?? undefined : undefined;
    }
    return undefined;
  };

  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      disableEditPopper={disableEditPopper}
      saveSuccess={() => {
        if (isGrouped) return;
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={() => {
        if (isGrouped) return;
        revertChanges();
      }}
      renderEditField={() => (
        <FormControl
          variant="outlined"
          fullWidth
          error={fieldState.invalid}
          required={rules?.required === true ?? false}
          disabled={disabled}
        >
          <InputLabel id={`lbl-${name}`}>
            {`${label || _startCase(name)}`}
          </InputLabel>
          <Select
            labelId={`lbl-${name}`}
            label={`${label || _startCase(name)}`}
            value={field.value as any || ''}
            onChange={(event: SelectChangeEvent<string>) => {
              if (isGrouped) return;
              field.onChange(event);

              if (onChange) {
                onChange(event);
              }
            }}
            onBlur={field.onBlur}
          >
            {menuItems.map((menuItem) => (
              menuItem.value
                ? (
                  <MenuItem key={menuItem.value} value={menuItem.value}>
                    {menuItem.name}
                  </MenuItem>
                )
                : (
                  <MenuItem value="">
                    <em>{menuItem.name}</em>
                  </MenuItem>
                )
            ))}
          </Select>
          {fieldState.invalid && fieldState.error && (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
      renderReadField={() => (
        <ReadOnlyField
          name={label || _startCase(name)}
          value={useMenuItemName ? getMenuItemName(field.value) : field.value}
        />
      )}
    />
  );
}

export default ControlledSelect;
